import { Box, HStack, Icon, Text, VStack } from '@chakra-ui/react'
import { FiMenu } from 'react-icons/fi'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensors,
  useSensor,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { TurfMemberDto } from '../../types/turf'

export interface TurfMemberReorderListProps {
  members: TurfMemberDto[]
  onMove: (memberId: string, toPosition: number) => void
}

/** Pure drop resolution: the dragged member and its 1-based target position, or null for a no-op. */
export function reorderTarget(members: { id: string }[], activeId: string, overId: string) {
  const to = members.findIndex(m => m.id === overId)
  return activeId === overId || to < 0 ? null : { memberId: activeId, toPosition: to + 1 }
}

function ReorderRow({ member }: { member: TurfMemberDto }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: member.id })
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }

  return (
    <HStack
      ref={setNodeRef}
      style={style}
      gap={3}
      px={4}
      py={2}
      bg="surface"
      borderBottomWidth="1px"
      borderColor="border"
    >
      <Icon color="ink.2" cursor="grab" aria-label={`Drag ${member.contactName}`} {...attributes} {...listeners} asChild>
        <FiMenu />
      </Icon>
      <Text fontWeight="medium" color="ink.2" minW="32px">{member.walkOrder}</Text>
      <Box minW={0}>
        <Text fontWeight="medium" lineClamp={1}>{member.contactName}</Text>
        <Text fontSize="sm" color="ink.2" lineClamp={1}>{member.streetAddress}</Text>
      </Box>
    </HStack>
  )
}

export function TurfMemberReorderList({ members, onMove }: TurfMemberReorderListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return
    const target = reorderTarget(members, String(active.id), String(over.id))
    if (target) onMove(target.memberId, target.toPosition)
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={members.map(m => m.id)} strategy={verticalListSortingStrategy}>
        <VStack gap={0} align="stretch">
          {members.map(m => <ReorderRow key={m.id} member={m} />)}
        </VStack>
      </SortableContext>
    </DndContext>
  )
}
